import React from 'react';

// App-wide safety net: a render error anywhere shows a recoverable message
// instead of a blank white screen. (Catches the class of crashes like a bad
// store coordinate flowing into a map/format call, or a third-party browser
// extension mutating the DOM React manages — see BUG-023.)
//
// The crash screen surfaces the real error + a one-click "Copy error details"
// so a non-technical user can relay exactly what happened (name, message, stack,
// React component stack, browser, URL) instead of just "it crashed".
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null, copied: false };
    this.copy = this.copy.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // eslint-disable-next-line no-console
    console.error('Render error caught by ErrorBoundary:', error, info);
  }

  details() {
    const { error, info } = this.state;
    return [
      `Error: ${error?.name || 'Error'}: ${error?.message || String(error)}`,
      `When: ${new Date().toISOString()}`,
      `URL: ${typeof window !== 'undefined' ? window.location.href : '—'}`,
      `Browser: ${typeof navigator !== 'undefined' ? navigator.userAgent : '—'}`,
      '',
      'Stack:',
      error?.stack || '(none)',
      '',
      'Component stack:',
      info?.componentStack || '(none)',
    ].join('\n');
  }

  async copy() {
    try {
      await navigator.clipboard.writeText(this.details());
      this.setState({ copied: true });
    } catch {
      this.setState({ copied: false });
    }
  }

  render() {
    const { error, copied } = this.state;
    if (error) {
      return (
        <div style={{
          minHeight: '100vh', display: 'grid', placeItems: 'center',
          background: 'var(--color-bg)', padding: 'var(--space-6)', textAlign: 'center',
        }}
        >
          <div style={{ maxWidth: 480 }}>
            <h2 style={{ margin: '0 0 8px' }}>Something went wrong</h2>
            <p className="muted" style={{ fontSize: 'var(--text-small)' }}>
              <b>{error?.name || 'Error'}:</b> {String(error?.message || error)}
            </p>
            <div className="row" style={{ justifyContent: 'center', gap: 8, marginTop: 'var(--space-4)' }}>
              <button className="btn btn-ghost" onClick={() => this.setState({ error: null, info: null, copied: false })}>Try again</button>
              <button className="btn btn-ghost" onClick={this.copy}>{copied ? 'Copied ✓' : 'Copy error details'}</button>
              <button className="btn btn-primary" onClick={() => window.location.reload()}>Reload</button>
            </div>
            <p className="muted" style={{ fontSize: 'var(--text-caption)', marginTop: 'var(--space-3)' }}>
              Tap “Copy error details” and paste it to the team so we can pinpoint the cause.
            </p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
